import React, { useRef, useState } from "react";
import { Box, FormHelperText, Typography } from "@mui/material";
import { Upload } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ButtonSecondary } from "src/UI/ButtonSecondary";

import { LogoType } from "../types";
import { LOGO_DESCRIPTORS, isValidLogoFile } from "../constants";

interface LogoUploadFieldProps {
  type: LogoType;
  value: string;
  onChange: (dataUrl: string) => void;
}

const LogoUploadField: React.FC<LogoUploadFieldProps> = ({
  type,
  value,
  onChange,
}) => {
  const { t } = useTranslation(["corporate-brand-asset"]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const descriptor = LOGO_DESCRIPTORS.find((d) => d.type === type)!;
  const darkBackdrop = type === "white";

  const handleFile = (file?: File) => {
    if (!file) return;
    if (!isValidLogoFile(file, type)) {
      setError(
        t("logos.invalidFormat", { format: descriptor.ext.toUpperCase() }) ||
          "",
      );
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result as string);
    reader.readAsDataURL(file);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        border: "1px solid",
        borderColor: error ? "error.main" : "divider",
        borderRadius: "8px",
        p: 2,
        height: "100%",
      }}
    >
      <Typography variant="subtitle2">{t(`logos.types.${type}`)}</Typography>
      <Box
        sx={{
          height: 96,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "4px",
          backgroundColor: darkBackdrop ? "#1a1a1a" : "background.default",
          overflow: "hidden",
        }}
      >
        {value ? (
          <Box
            component="img"
            src={value}
            alt={type}
            sx={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
          />
        ) : (
          <Typography variant="caption" color="text.primary">
            {t("logos.noImage")}
          </Typography>
        )}
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept={descriptor.accept}
        hidden
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <ButtonSecondary
        size="sm"
        onClick={() => inputRef.current?.click()}
        startIcon={<Upload size={14} />}
      >
        {value ? t("logos.replace") : t("logos.upload")}
      </ButtonSecondary>
      <Typography variant="caption" color="text.secondary">
        {descriptor.ext.toUpperCase()}
      </Typography>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

export default LogoUploadField;
